import { apiClient } from "../api/apiClient";
import { tokenService } from "../storage/tokenService";
import { API_ENDPOINTS } from "../../utils/constants";
import type { UserInfo } from "../../types/auth";

export interface ProfilePayload {
  first_name?: string;
  last_name?: string;
  email?: string;
  name?: string;
  phone_number?: string;
  language?: string;
  /** Optional File object — when provided, the request is sent as multipart. */
  avatar?: File | null;
}

export interface ChangePasswordPayload {
  old_password: string;
  new_password: string;
  new_password_confirm: string;
}

/**
 * Profile / password operations against /api/auth/*.
 * `updateProfile` accepts both JSON and multipart payloads transparently —
 * if `avatar` is a File, the request is sent as FormData.
 */
export const profileService = {
  async getMe(): Promise<UserInfo> {
    const response = await apiClient.get<UserInfo>(API_ENDPOINTS.AUTH.ME);
    tokenService.setUserInfo(response.data);
    return response.data;
  },

  async updateProfile(payload: ProfilePayload): Promise<UserInfo> {
    const hasFile = payload.avatar instanceof File;
    let body: FormData | ProfilePayload;

    if (hasFile) {
      const fd = new FormData();
      Object.entries(payload).forEach(([key, value]) => {
        if (value === undefined || value === null) return;
        if (key === "avatar" && value instanceof File) {
          fd.append("avatar", value);
        } else {
          fd.append(key, String(value));
        }
      });
      body = fd;
    } else {
      const { avatar: _avatar, ...rest } = payload;
      body = rest;
    }

    // No pasamos `headers` manualmente. El interceptor de `apiClient`
    // detecta que `config.data instanceof FormData` y borra el
    // `Content-Type: application/json` que la instancia trae por
    // defecto — así axios recalcula el `multipart/form-data;
    // boundary=<uuid>` correcto.
    //
    // Antes se hacía `headers = { "Content-Type": undefined }` aquí,
    // pero en axios v1 ese `undefined` acaba serializado como el
    // literal string "undefined" en el header, con lo que Django no
    // parsea el body como multipart y el campo `avatar` se descarta
    // silenciosamente (el resto de campos sí llegan como form data).
    // Síntoma: el usuario guardaba, el PATCH devolvía 200, pero el
    // avatar quedaba a null en BBDD.
    const response = await apiClient.patch<UserInfo>(
      API_ENDPOINTS.AUTH.ME,
      body,
    );
    tokenService.setUserInfo(response.data);
    return response.data;
  },

  async changePassword(payload: ChangePasswordPayload): Promise<void> {
    await apiClient.post(API_ENDPOINTS.AUTH.CHANGE_PASSWORD, payload);
  },
};

export default profileService;
